import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router'
import { ENVIRONMENT } from './environment.config'
import {
  isClientNoise,
  redactBreadcrumb,
  redactDeep,
  redactEventUrls,
  shouldSampleReplay,
  THIRD_PARTY_NOISE_PATTERNS,
} from './sentry.config.helpers'

export const SENTRY_CONFIG: Sentry.BrowserOptions = {
  dsn: ENVIRONMENT.SENTRY_DSN,
  // Local dev shares the staging project but stays silent by default — set
  // VITE_SENTRY_ENABLE_LOCAL=true to emit while testing. Deployed envs send.
  enabled:
    !import.meta.env.DEV ||
    import.meta.env.VITE_SENTRY_ENABLE_LOCAL === 'true',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      createRoutesFromChildren: createRoutesFromChildren,
      useLocation: useLocation,
      useNavigationType: useNavigationType,
      matchRoutes: matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      // rrweb's page-href meta can't be scrubbed, so never replay it
      beforeErrorSampling: () => shouldSampleReplay(window.location.pathname),
      beforeAddRecordingEvent: (event) => redactDeep(event),
    }),
    // Capture console.warn/console.error in the browser as Sentry logs.
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  // Sentry Logs: surface browser warnings/errors as structured logs,
  // correlated with the page's trace.
  enableLogs: true,
  // Fraction of page loads traced. Full in dev; 0.1 in prod to keep the
  // performance quota (and cost) bounded — raise temporarily to debug latency.
  tracesSampleRate: import.meta.env.DEV ? 1 : 0.1,
  // Session Replay is Sentry's priciest feature, so DON'T record every session:
  // 0% of normal sessions + 100% of sessions that hit an error — you get a
  // replay exactly when something broke, at near-zero quota cost.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  // Injected at build time from the git commit (see vite.config.ts) so
  // frontend issues in Sentry are attributed to a specific deploy.
  release: import.meta.env.VITE_SENTRY_RELEASE,
  attachStacktrace: true,
  // Errors thrown by injected third-party scripts, not our code.
  ignoreErrors: THIRD_PARTY_NOISE_PATTERNS,
  debug: false,
  maxBreadcrumbs: 50,
  // Strip bearer-credential query params (the waitlist magic-link `wlt`,
  // any `token`) from navigation/fetch/xhr breadcrumb urls before they
  // are stored. The SDK auto-captures full navigation urls, so without
  // this the live token would ship to Sentry and be replayable.
  beforeBreadcrumb: redactBreadcrumb,
  // Same scrub for the error event's request url and transaction name —
  // the route pattern is parameterised, but the raw url can still carry
  // the token, and `transaction` may hold the full path.
  // Client connectivity — a dropped request, an aborted one, Firebase
  // unable to reach Identity Platform, WebKit dropping its IndexedDB
  // connection — is the browser's condition, not ours. Errors that got
  // a response are real and kept.
  beforeSend: (event, hint) =>
    isClientNoise(hint.originalException) ? null : redactEventUrls(event),
  // Transactions are sent through a separate hook; scrub them too so the
  // token can't leak via a performance/tracing event.
  beforeSendTransaction: redactEventUrls,
}
